import { Typography } from '@material-ui/core';
import { ColorDynamic, Inline, Tag } from '@superdispatch/ui';
import { ButtonProps } from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import {
  LoadDTO,
  formatLoadStage,
  formatLoadStatus,
  isLoadCanceled,
} from '../../data/LoadDTO';
import { formatStageMedium, trackLoadsEvent } from '../../data/LoadsAnalytics';
import { LoadsPageParams } from '../../data/LoadsPageParams';
import { isEmpty, omitBy } from 'es-toolkit/compat';
import { ReactNode } from 'react';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import {
  canHaveSimilarSearch,
  createSearchCriteria,
  createSimilarSearch,
  searchCriteriaToJSON,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { PermissionDTO } from 'shared/modules/permission/PermissionDTO';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import {
  CarrierSettings,
  FeatureToggle,
} from 'shared/settings/CarrierSettingsAPI';
import { LoadCreationLockIcon } from '../core/LoadCreationLockIcon';

export type LoadsPageLoadAction =
  | 'assign'
  | 'assign_trip'
  | 'reassign'
  | 'unassign'
  | 'duplicate'
  | 'add_note'
  | 'instant_transfer'
  | 'mark_as_new'
  | 'mark_as_picked_up'
  | 'mark_as_delivered'
  | 'mark_as_paid'
  | 'mark_as_unpaid'
  | 'mark_as_paid_to_subhauler'
  | 'mark_as_unpaid_to_subhauler'
  | 'revoke'
  | 'send_bol'
  | 'view_bol'
  | 'view_online_bol'
  | 'view_subhauler_bol'
  | 'archive'
  | 'unarchive'
  | 'delete'
  | 'restore'
  | 'cancel'
  | 'add_terminal'
  | 'remove_terminal'
  | 'mark_as_in_terminal'
  | 'remove_dispatched_to_carrier_label'
  | 'request_access_dispatch_to_carrier'
  | 'dispatch_to_carrier'
  | 'expedite_payment'
  | 'send_invoice_drawer'
  | 'upgrade_plan'
  | 'upgrade_plan_warning'
  | 'move_to_vehicles'
  | 'move_to_assigned'
  | 'move_to_unassigned'
  | 'subhaul';

export interface LoadsPageLoadActionProps {
  load: LoadDTO;
  settings?: CarrierSettings;
  params: LoadsPageParams;
  isMobile: boolean;
  isFeatureToggleEnabled: (feature: FeatureToggle) => boolean;
}

interface LoadsPageLoadActionOption {
  label: ReactNode | ((props: LoadsPageLoadActionProps) => ReactNode);
  dataIntercomTarget?: string;
  action?: (props: LoadsPageLoadActionProps) => LoadsPageLoadAction;
  onClick?: (props: LoadsPageLoadActionProps) => void;
  isPrimary?: (props: LoadsPageLoadActionProps) => boolean;
  isVisible?: (props: LoadsPageLoadActionProps) => boolean;
  isDisabled?: (props: LoadsPageLoadActionProps) => boolean;
  buttonProps?: (props: LoadsPageLoadActionProps) => Partial<ButtonProps>;
  permission?: keyof NonNullable<PermissionDTO['loads']>;
  endIcon?: ReactNode;
  startIcon?: ReactNode | undefined;
}

interface LoadsPageLoadActionGroup {
  options: LoadsPageLoadActionOption[];
  isVisible?: (props: LoadsPageLoadActionProps) => boolean;
}

export const actions: LoadsPageLoadActionGroup[] = [
  {
    isVisible: ({ load }) => load.is_dispatched_to_carrier,
    options: [
      {
        label: 'Open Dispatched Load',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        onClick: ({ load }) => {
          openExternalURL(load.url_in_stms, { target: '_blank' });
        },
      },
      {
        label: 'Remove "Dispatched to Carrier"',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'remove_dispatched_to_carrier_label',
      },
    ],
  },

  {
    isVisible: ({ load, isMobile }) => !!load.can_be_edited && isMobile,
    options: [
      {
        label: 'Edit',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        onClick: ({ load, params }) => {
          openExternalURL(
            parseURITemplate('/tms/loads/{guid}/edit{?next}', {
              guid: load.guid,
              next: window.location.pathname + window.location.search,
            }),
          );

          trackLoadsEvent({
            name: 'Carrier Clicked Edit Load',
            page: 'loads_list',
            is_created_by_broker: !!load.is_created_by_broker,
            load_status: formatLoadStatus(load.status),
            utm_medium:
              params.stage === 'active'
                ? 'Search Results'
                : formatLoadStage(params.stage),
          });
        },
      },
    ],
  },

  {
    isVisible: ({ load, settings }) =>
      !!settings?.has_terminal && !load.archived && load.is_active,

    options: [
      {
        label: 'Mark as in Terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'mark_as_in_terminal',
        isVisible: ({ load }) =>
          !!load.terminals.length && load.can_mark_as_in_terminal,
      },
      {
        label: 'Add Terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'add_terminal',
        isVisible: ({ load }) =>
          load.can_add_terminal && !load.is_received_via_subhaul,
      },
      {
        label: 'Remove Terminal',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'remove_terminal',
        isVisible: ({ load }) =>
          load.terminals.some((terminal) => !terminal.is_delivered),
      },
    ],
  },
  {
    isVisible: ({ load }) => load.is_active,

    options: [
      {
        label: 'Assign',
        permission: 'can_manage',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: ({ load }) => (load.trip?.name ? 'assign_trip' : 'assign'),
        isVisible: ({ load }) =>
          !load.archived && load.can_be_assigned_to_driver,
        buttonProps: ({ load }) => ({
          variant: load.stage === 'picked_up' ? 'neutral' : 'default',
        }),
        dataIntercomTarget: 'load-assign-button',
        isPrimary: ({ load }) =>
          load.stage === 'new' ||
          load.stage === 'in_terminal' ||
          (load.stage === 'picked_up' && !load.can_be_unassigned_from_driver),
      },

      {
        label: 'Reassign',
        permission: 'can_manage',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: ({ load }) => (load.trip?.name ? 'reassign' : 'assign'),
        buttonProps: ({ load }) => ({
          variant: load.stage === 'in_terminal' ? 'default' : 'neutral',
        }),
        isVisible: ({ load }) => load.can_be_unassigned_from_driver,
        isPrimary: ({ load }) =>
          load.stage === 'in_terminal' ||
          load.stage === 'picked_up' ||
          load.stage === 'assigned',
      },

      {
        label: 'Unassign',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        action: () => 'unassign',
        isVisible: ({ load }) =>
          !load.archived && load.can_be_unassigned_from_driver,
      },

      {
        label: 'Duplicate',
        action: ({ settings }) =>
          settings?.can_create_load ? 'duplicate' : 'upgrade_plan',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          >
            <LoadCreationLockIcon />
          </PermissionIcon>
        ),
        isVisible: ({ load, isFeatureToggleEnabled }) =>
          !load.archived &&
          load.can_be_duplicated &&
          !load.is_received_via_subhaul &&
          (!load.is_edi ||
            isFeatureToggleEnabled('carrier_duplicate_edi_load')),
      },

      {
        label: 'Split Order',
        isVisible: ({ load, isFeatureToggleEnabled }) =>
          !load.archived &&
          load.can_be_split &&
          !load.is_received_via_subhaul &&
          (!load.is_edi ||
            isFeatureToggleEnabled('carrier_duplicate_edi_load')),
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        onClick: ({ load, params }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Split Order',
            page: 'loads_list',
            utm_medium: formatStageMedium(params.stage),
            is_created_by_broker: load.is_created_by_broker,
          });
          openExternalURL(
            '/tms/loads-split/{guid}{?back_url,is_created_by_broker,utm_medium}',
            {
              guid: load.guid,
              back_url: window.location.pathname + window.location.search,
              utm_medium: formatStageMedium(params.stage),
              is_created_by_broker: load.is_created_by_broker,
            },
          );
        },
      },
      {
        label: 'Sub-haul',
        action: () => 'subhaul',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isFeatureToggleEnabled }) =>
          !!load.can_be_subhauled && isFeatureToggleEnabled('ctms_edi_subhaul'),
      },
      {
        label: ({ isFeatureToggleEnabled, load }) =>
          isFeatureToggleEnabled('ctms_loads_new')
            ? load.driver?.guid
              ? 'Move to Assigned'
              : 'Move to Unassigned'
            : 'Mark as New',
        action: ({ load, isFeatureToggleEnabled }) =>
          isFeatureToggleEnabled('ctms_loads_new')
            ? load.driver?.guid
              ? 'move_to_assigned'
              : 'move_to_unassigned'
            : 'mark_as_new',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load }) => {
          if (load.payment_details?.payment_method === 'superpay') {
            return load.status === 'picked_up';
          }

          return load.status !== 'new';
        },
      },

      {
        label: 'Mark as Picked Up',
        action: () => 'mark_as_picked_up',
        permission: 'can_mark_as_picked_up',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_picked_up"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load }) => {
          const isVisible = !load.archived && load.can_be_picked_up;

          if (load.payment_details?.payment_method === 'superpay') {
            return load.status === 'new' && isVisible;
          }

          return isVisible;
        },
      },

      {
        label: 'Mark as Delivered',
        action: () => 'mark_as_delivered',
        permission: 'can_mark_as_delivered',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_delivered"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        buttonProps: () => ({ variant: 'neutral' }),
        isVisible: ({ load }) =>
          !load.archived &&
          load.can_be_delivered &&
          !load.can_mark_as_in_terminal,
      },
      {
        label: 'Dispatch to Carrier',
        action: () => 'dispatch_to_carrier',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) =>
          !load.archived &&
          load.can_be_dispatched_to_carrier &&
          !load.should_request_dispatch_to_carrier,
      },

      {
        label: 'Send BOL',
        action: () => 'send_bol',
        permission: 'can_send_bol',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_bol"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => load.can_send_bol,
        onClick: ({ load, params }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Send BOL',
            page: 'loads_list',
            utm_medium: formatStageMedium(params.stage),
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },

      {
        label: 'Move to Vehicles',
        action: () => 'move_to_vehicles',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load, isFeatureToggleEnabled }) =>
          !!load.is_edi && isFeatureToggleEnabled('ctms_edi_vehicles'),
        onClick: ({ load, params }) => {
          trackLoadsEvent({
            name: 'Carrier Clicked Move to Vehicles',
            page: 'loads_list',
            utm_medium: formatStageMedium(params.stage),
            status: load.status,
          });
        },
      },

      {
        label: 'Send Invoice',
        action: () => 'send_invoice_drawer',
        dataIntercomTarget: 'send-invoice-button',
        permission: 'can_send_invoice',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_invoice"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_send_invoice"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isPrimary: ({ load }) => load.stage === 'delivered',
        isVisible: ({ load }) => load.can_send_invoice,
        onClick: ({ load, params }) => {
          trackLoadsEvent({
            name: 'CTMS: Send Invoice Clicked',
            page: 'loads_list',
            load_stage: load.stage,
            utm_medium: formatStageMedium(params.stage),
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },

      {
        label: 'Mark as Paid',
        action: () => 'mark_as_paid',
        permission: 'can_mark_as_paid',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isPrimary: ({ load }) => load.stage === 'billed',
        isVisible: ({ load }) => !load.archived && load.can_be_marked_as_paid,
      },

      {
        label: 'Mark as Unpaid',
        action: () => 'mark_as_unpaid',
        permission: 'can_mark_as_paid',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_mark_as_paid"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => !!load.can_be_marked_as_unpaid,
      },

      { label: 'Add Internal Note', action: () => 'add_note' },
    ],
  },
  {
    options: [
      {
        label: (
          <Inline space="small">
            <Typography>Instant Transfer</Typography>
            <Tag color="blue" variant="subtle">
              New
            </Tag>
          </Inline>
        ),
        action: () => 'instant_transfer',
        isVisible: ({ load }) => !!load.payments[0]?.instant_transfer_available,
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
      },
      {
        label: 'View BOL',
        action: () => 'view_bol',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_view_bol"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        permission: 'can_view_bol',
        isVisible: ({ load }) => load.can_view_bol,
        onClick: ({ load, params }) => {
          trackLoadsEvent({
            name: 'CTMS: Clicked View BOL',
            page: 'loads_list',
            utm_medium: formatStageMedium(params.stage),
            status: load.status,
            is_canceled_by: load.is_canceled_by,
          });
        },
      },
    ],
  },
  {
    isVisible: ({ load }) => !load.archived && load.is_active,
    options: [
      {
        label: 'Search for Similar Loads',
        isDisabled: ({ load }) =>
          !canHaveSimilarSearch(
            createSearchCriteria({
              pickup_venues: [load.pickup.venue],
              delivery_venues: [load.delivery.venue],
            }),
          ),
        onClick: ({ load }) => {
          trackLoadsEvent({
            name: 'CTMS: Search for Similar Loads Clicked',
            page: 'loads_list',
          });
          openExternalURL(
            '/loadboard/loads{?utm_medium,utm_source,search_criteria}',
            {
              utm_source: 'Web CTMS',
              utm_medium: 'Similar Loads',
              search_criteria: searchCriteriaToJSON(
                createSimilarSearch(
                  createSearchCriteria({
                    pickup_venues: [omitBy(load.pickup.venue, isEmpty)],
                    delivery_venues: [omitBy(load.delivery.venue, isEmpty)],
                  }),
                ),
              ),
            },
          );
        },
      },
    ],
  },
  {
    options: [
      {
        label: 'Archive',
        action: () => 'archive',
        buttonProps: () => ({ variant: 'neutral' }),
        isPrimary: ({ load }) => load.stage === 'paid',
        permission: 'can_manage',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => {
          if (load.is_received_via_subhaul) {
            return (
              !load.archived &&
              load.is_active &&
              (!load.is_created_by_broker ||
                isLoadCanceled(load) ||
                load.status !== 'new') &&
              load.stage !== 'new' &&
              load.stage !== 'in_terminal' &&
              load.stage !== 'assigned' &&
              load.stage !== 'picked_up'
            );
          }
          return (
            !load.archived &&
            load.is_active &&
            (!load.is_created_by_broker ||
              isLoadCanceled(load) ||
              load.status !== 'new')
          );
        },
      },
      {
        label: 'Unarchive',
        action: () => 'unarchive',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => load.archived && load.is_active,
      },
      {
        label: 'Delete',
        action: () => 'delete',
        permission: 'can_delete',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_delete"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => {
          if (load.is_received_via_subhaul) {
            return (
              load.is_active &&
              (!load.is_created_by_broker ||
                isLoadCanceled(load) ||
                load.status !== 'new') &&
              load.stage !== 'new' &&
              load.stage !== 'in_terminal' &&
              load.stage !== 'assigned' &&
              load.stage !== 'picked_up'
            );
          }
          return (
            load.is_active &&
            (!load.is_created_by_broker ||
              isLoadCanceled(load) ||
              load.status !== 'new')
          );
        },
      },
      {
        label: 'Cancel Load',
        action: () => 'cancel',
        permission: 'can_manage',
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => {
          const isInterminal = load.terminals.some((t) => t.is_current);
          return (
            load.is_created_by_broker &&
            !isLoadCanceled(load) &&
            load.is_active &&
            load.status === 'new' &&
            !isInterminal
          );
        },
      },
      {
        label: 'Restore',
        action: () => 'restore',
        permission: 'can_manage',
        startIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            fontSize="medium"
          />
        ),
        endIcon: (
          <PermissionIcon
            name="loads"
            permission="can_manage"
            htmlColor={ColorDynamic.Dark100}
            fontSize="medium"
          />
        ),
        isVisible: ({ load }) => !load.is_active,
        buttonProps: () => ({ variant: 'neutral' }),
        isPrimary: ({ load }) => load.stage === 'deleted',
      },
    ],
  },
];
